import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/providers';
import { toast } from 'sonner';

const passwordSchema = Yup.object({
    currentPassword: Yup.string().required('Current password is required'),
    newPassword: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
            'Password must contain at least one uppercase letter, one lowercase letter, and one number'
        )
        .required('New password is required'),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword')], 'Passwords must match')
        .required('Please confirm your new password'),
});

export function SecuritySettings() {
    const { updatePassword } = useAuth();
    const [isLoading, setIsLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
        },
        validationSchema: passwordSchema,
        onSubmit: async (values, { resetForm }) => {
            setIsLoading(true);
            try {
                await updatePassword(values.currentPassword, values.newPassword);
                toast.success('Password updated successfully');
                resetForm();
            } catch (error: any) {
                toast.error(error.message || 'Failed to update password');
            } finally {
                setIsLoading(false);
            }
        },
    });

    return (
        <Card className="p-6">
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-medium">Password & Security</h3>
                    <p className="text-sm text-muted-foreground">
                        Update your password to keep your account secure.
                    </p>
                </div>

                <Separator />

                <form onSubmit={formik.handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="currentPassword">Current Password</Label>
                        <Input
                            id="currentPassword"
                            name="currentPassword"
                            type="password"
                            placeholder="Enter your current password"
                            value={formik.values.currentPassword}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={formik.touched.currentPassword && formik.errors.currentPassword ? 'border-destructive' : ''}
                        />
                        {formik.touched.currentPassword && formik.errors.currentPassword && (
                            <p className="text-sm text-destructive">{formik.errors.currentPassword}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="newPassword">New Password</Label>
                        <Input
                            id="newPassword"
                            name="newPassword"
                            type="password"
                            placeholder="Enter your new password"
                            value={formik.values.newPassword}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={formik.touched.newPassword && formik.errors.newPassword ? 'border-destructive' : ''}
                        />
                        {formik.touched.newPassword && formik.errors.newPassword && (
                            <p className="text-sm text-destructive">{formik.errors.newPassword}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="confirmPassword">Confirm New Password</Label>
                        <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            type="password"
                            placeholder="Confirm your new password"
                            value={formik.values.confirmPassword}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={formik.touched.confirmPassword && formik.errors.confirmPassword ? 'border-destructive' : ''}
                        />
                        {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                            <p className="text-sm text-destructive">{formik.errors.confirmPassword}</p>
                        )}
                    </div>

                    <div className="bg-muted/50 rounded-lg p-4">
                        <h4 className="text-sm font-medium mb-2">Password Requirements:</h4>
                        <ul className="text-sm text-muted-foreground space-y-1">
                            <li>• At least 8 characters long</li>
                            <li>• Contains at least one uppercase letter</li>
                            <li>• Contains at least one lowercase letter</li>
                            <li>• Contains at least one number</li>
                        </ul>
                    </div>

                    <div className="flex justify-end">
                        <Button
                            type="submit"
                            disabled={isLoading || !formik.isValid || !formik.dirty}
                        >
                            {isLoading ? 'Updating...' : 'Update Password'}
                        </Button>
                    </div>
                </form>
            </div>
        </Card>
    );
}
